import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Badge, Box, Callout, Column, Row, Typography } from 'seagull';
import { Bulb } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { useResponsive } from '~/client-flow/contexts';
import { buildSystemVariableResolution } from '~/components/AgreementStudioPage/TemplateVariables/TemplateVariables.utils';
import { VariableInlineRenderer } from '~/components/AgreementStudioPage/TemplateVariables/VariableInlineRenderer/VariableInlineRenderer';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { AgreementDataModel, relationshipModel } from '~/models';
import { toDate } from '~/models/time/translator';
import { getFutureBillingText } from '~/utils/service-description/getFutureBillingText';
import { billingBadgesTextsPath, billingDetailsBadge, discountBadge as getDiscountBadge } from '~/utils/service-description/getServiceDescriptionBadges';
import { renderServicePrice } from '~/utils/service-description/renderServicePrice';
import { ServiceDescription } from '../../../components/ServiceDescription/ServiceDescription';
import styles from './PackageDetailsService.module.scss';
export const GetPackageDetailsServiceTID = (parentTestId)=>getTestIds({
        parentTestId
    });
export const PackageDetailsService = (param)=>{
    let { service, relationship, now, resolvedValues: providedResolvedValues, agreementData: providedAgreementData, testId } = param;
    const { PackageDetailsServiceTestIds: tids } = useMemo(()=>GetPackageDetailsServiceTID(testId), [
        testId
    ]);
    const featureFlags = useFeatureFlags();
    const { isMobile } = useResponsive();
    const { t: billingBadgesTexts } = useLocaleKeys(billingBadgesTextsPath);
    const { t: reviewTexts } = useLocaleKeys('clientFlow.reviewServices');
    const { t: calloutsTexts } = useLocaleKeys('billing.callouts');
    const computedAgreementData = useMemo(()=>AgreementDataModel.fromRelationship(relationship), [
        relationship
    ]);
    const agreementData = providedAgreementData !== null && providedAgreementData !== void 0 ? providedAgreementData : computedAgreementData;
    const resolvedValues = useMemo(()=>{
        var _relationship_buyer_firstName, _relationship_buyer_lastName;
        return providedResolvedValues !== null && providedResolvedValues !== void 0 ? providedResolvedValues : buildSystemVariableResolution({
            clientCompanyName: relationshipModel.customerName(relationship),
            clientFirstName: (_relationship_buyer_firstName = relationship.buyer.firstName) !== null && _relationship_buyer_firstName !== void 0 ? _relationship_buyer_firstName : undefined,
            clientLastName: (_relationship_buyer_lastName = relationship.buyer.lastName) !== null && _relationship_buyer_lastName !== void 0 ? _relationship_buyer_lastName : undefined,
            clientEmail: relationship.buyer.email,
            vendorBusinessName: relationship.vendor.name,
            agreementName: relationship.title,
            effectiveDate: agreementData.effectiveDate ? toDate(agreementData.effectiveDate) : null,
            currentDate: now
        });
    }, [
        providedResolvedValues,
        relationship,
        agreementData.effectiveDate,
        now
    ]);
    const discountBadgeData = getDiscountBadge(service, billingBadgesTexts);
    const billingBadgeText = billingDetailsBadge(service, {
        t: billingBadgesTexts,
        effectiveDate: agreementData.effectiveDate,
        isClientPreview: true,
        now
    }).text;
    const futureBillingText = getFutureBillingText(service, now, calloutsTexts);
    const useInlineFutureBilling = featureFlags.CLIENT_ONBOARDING_NEW_FLOW;
    const nameNode = /*#__PURE__*/ _jsx(Typography, {
        variant: "paragraph2",
        weight: "normal",
        skin: "standard",
        className: styles.serviceName,
        testId: tids.ServiceName(),
        children: /*#__PURE__*/ _jsx(VariableInlineRenderer, {
            text: service.name,
            resolvedValues: resolvedValues
        })
    });
    const badgesNode = /*#__PURE__*/ _jsxs(Row, {
        gap: 8,
        className: styles.badges,
        children: [
            discountBadgeData && /*#__PURE__*/ _jsx(Badge, {
                rounded: true,
                outlined: true,
                skin: "primary",
                children: discountBadgeData.text
            }),
            /*#__PURE__*/ _jsx(Badge, {
                rounded: true,
                outlined: true,
                skin: "primary",
                children: billingBadgeText
            })
        ]
    });
    const priceNode = /*#__PURE__*/ _jsx(Box, {
        className: styles.price,
        testId: tids.Price(),
        children: renderServicePrice(service, reviewTexts)
    });
    return /*#__PURE__*/ _jsxs(Column, {
        gap: 12,
        className: styles.service,
        testId: testId,
        children: [
            isMobile ? /*#__PURE__*/ _jsxs(Column, {
                gap: 8,
                className: styles.serviceHeaderMobile,
                children: [
                    nameNode,
                    priceNode,
                    badgesNode
                ]
            }) : /*#__PURE__*/ _jsxs(Row, {
                gap: 16,
                alignItems: "center",
                className: styles.serviceHeader,
                children: [
                    nameNode,
                    badgesNode,
                    priceNode
                ]
            }),
            /*#__PURE__*/ _jsx(ServiceDescription, {
                service: service,
                relationship: relationship,
                now: now,
                resolvedValues: resolvedValues
            }),
            futureBillingText && (useInlineFutureBilling ? /*#__PURE__*/ _jsxs(Row, {
                gap: 8,
                alignItems: "center",
                className: styles.futureBillingInline,
                children: [
                    /*#__PURE__*/ _jsx(Box, {
                        className: styles.futureBillingIcon,
                        children: /*#__PURE__*/ _jsx(Bulb, {})
                    }),
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph2",
                        weight: "light",
                        testId: tids.FutureBillingText(),
                        children: futureBillingText
                    })
                ]
            }) : /*#__PURE__*/ _jsx(Callout, {
                variant: "paragraph2",
                intent: "info",
                icon: /*#__PURE__*/ _jsx(Bulb, {}),
                testId: tids.FutureBillingText(),
                children: futureBillingText
            }))
        ]
    });
};
